import { useState, useEffect } from 'react'
import firebase from '../firebase/compat'
import { useEmptyStateContext } from '../StateManagment/EmptyStateContext'
import { isBenchmarkStrongEnough } from '../helpers/dataHelpers/getBenchmarkStrength'
import { client } from './Client'

/**
 * Fetches the cross-company MSI benchmark for one effect via the `msiBenchmark`
 * Cloud Function. Gated on the empty-state context: it only fetches on internal
 * cards (dashboard / Analyse), never on public LiveReports/print (no provider →
 * `showCta` falsy), so benchmark figures stay internal.
 *
 * Benchmarks below 'Sterke benchmark' strength are not used as steering data,
 * but the hook exposes that reason so cards can distinguish weak benchmarks
 * from truly missing benchmark data.
 *
 * Lifted out of GrowthAndBenchmark so a card can fetch once and share the result
 * with both the chart overlay and the summary caption.
 * AI-CONTEXT: See docs/features/msi-effect-unified-chart.md
 *
 * @param {string} effectId - OutputEffects.ID to benchmark
 * @returns {{ benchmark: object | null, loading: boolean, benchmarkUnavailableReason: 'weak' | 'missing' | null }}
 */
const useMsiBenchmark = (effectId) => {
  const [benchmark, setBenchmark] = useState(null)
  const [loading, setLoading] = useState(false)
  const [benchmarkUnavailableReason, setBenchmarkUnavailableReason] = useState(null)

  const { showCta: onInternalCard } = useEmptyStateContext()

  useEffect(() => {
    if (!effectId) return
    if (!onInternalCard) return

    let active = true
    setLoading(true)
    setBenchmark(null)
    setBenchmarkUnavailableReason(null)

    const msiBenchmark = firebase.app().functions('europe-west1').httpsCallable('msiBenchmark')

    msiBenchmark({ effectId, callerCompanyId: client })
      .then((result) => {
        if (!active) return
        const nextBenchmark = result.data || null
        if (!nextBenchmark || nextBenchmark.benchmarkGrowth == null) {
          setBenchmark(null)
          setBenchmarkUnavailableReason('missing')
          return
        }
        if (!isBenchmarkStrongEnough(nextBenchmark)) {
          setBenchmark(null)
          setBenchmarkUnavailableReason('weak')
          return
        }
        setBenchmark(nextBenchmark)
        setBenchmarkUnavailableReason(null)
      })
      .catch((err) => console.error('useMsiBenchmark error:', err))
      .finally(() => { if (active) setLoading(false) })

    return () => { active = false }
  }, [effectId, onInternalCard])

  return { benchmark, loading, benchmarkUnavailableReason }
}

export default useMsiBenchmark
